import { SECURITY_MODULES, type SecurityModuleMeta, type TrainingLevel } from "../data/securityModules";
import { authFetch } from "./authFetch";
import { defaultTrainingPlans, trainingPlanRank, userTrainingPlanRank, type TrainingPlan } from "./trainingPlans";

export type ModulePlanOverrides = Partial<Record<string, TrainingLevel>>;
type EntitlementModulePlanResponse = {
  features?: {
    module_plan_overrides?: unknown;
  };
};
export type UserGroupProfile = {
  // Die im Profil hinterlegte Schulungsstufe. Fehlt sie, liefert die API die
  // Mandanten-Vorgabe (features.default_level) – dieselbe Reihenfolge, die der
  // Server für Monatsmails und Auswertungen verwendet.
  level?: TrainingLevel | null;
  department?: string | null;
  departments?: Array<string | null | undefined> | null;
};

const FALLBACK_PLANS = defaultTrainingPlans("de");

/**
 * Kumulative Sichtbarkeit: Ein Modul auf Plan X ist sichtbar für alle Nutzer,
 * deren Plan-Rang >= Rang(X) ist (der erste Plan sieht jeder, der letzte Plan sieht alles).
 * `plans` bestimmt Reihenfolge/Rang; ohne Angabe gelten die 4 Standard-Pläne.
 */
export function isModuleLevelVisibleForUser(userLevel: TrainingLevel, moduleLevel: TrainingLevel, plans: TrainingPlan[] = FALLBACK_PLANS): boolean {
  return userTrainingPlanRank(userLevel, plans) >= trainingPlanRank(moduleLevel, plans);
}

export function normalizeModulePlanOverrides(raw: unknown): ModulePlanOverrides {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const moduleIds = new Set(SECURITY_MODULES.map(m => m.id));
  const normalized: ModulePlanOverrides = {};
  Object.entries(raw as Record<string, unknown>).forEach(([moduleId, level]) => {
    if (!moduleIds.has(moduleId)) return;
    if (typeof level !== "string" || !level.trim() || level.length > 60) return;
    const original = SECURITY_MODULES.find(m => m.id === moduleId)?.level;
    if (level !== original) normalized[moduleId] = level;
  });
  return normalized;
}

export function loadModulePlanOverrides(tenantId: string): ModulePlanOverrides {
  void tenantId;
  return {};
}

export async function fetchModulePlanOverrides(tenantId: string): Promise<ModulePlanOverrides> {
  if (!tenantId) return {};
  try {
    const res = await authFetch(`/api/entitlements?tenantId=${encodeURIComponent(tenantId)}`);
    if (!res.ok) return {};
    const data = await res.json() as EntitlementModulePlanResponse | null;
    const features = data?.features;
    if (!features || !Object.prototype.hasOwnProperty.call(features, "module_plan_overrides")) return {};
    return normalizeModulePlanOverrides(features.module_plan_overrides);
  } catch {
    return {};
  }
}

export async function saveModulePlanOverridesForTenant(tenantId: string, overrides: ModulePlanOverrides): Promise<boolean> {
  if (!tenantId) return false;
  const normalized = normalizeModulePlanOverrides(overrides);
  try {
    const res = await authFetch("/api/entitlements", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, modulePlanOverridesPatch: { overrides: normalized } }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Modul-Lizenzen: die im GA-Portal für den Mandanten freigeschalteten Module.
 * Fehlt das Feld, ist nichts lizenziert — Module sind bewusst opt-in.
 * Gegenstück zu `active_modules`: Lizenzen kommen von außen, das Kontingent
 * stellt der Tenant-Admin selbst ein.
 */
export function readLicensedModules(features: { licensed_modules?: unknown } | null | undefined): string[] {
  if (!features || typeof features !== "object") return [];
  const raw = features.licensed_modules;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === "string" && id.trim().length > 0).map(id => id.trim());
}

/**
 * Modul-Kontingent: Liest die vom Admin aktivierten Modul-IDs aus den Features.
 * `null` = nicht konfiguriert → alle Module gelten als aktiv (Rückwärtskompatibilität).
 * `[]`   = bewusst keine Module aktiv.
 */
export function readActiveModules(features: { active_modules?: unknown } | null | undefined): string[] | null {
  if (!features || typeof features !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(features, "active_modules")) return null;
  const raw = features.active_modules;
  if (!Array.isArray(raw)) return null;
  return raw.filter((id): id is string => typeof id === "string" && id.trim().length > 0);
}

/** Filtert eine Modul-Liste auf das Tenant-Kontingent. `null` = kein Limit, alle bleiben. */
export function filterActiveModules<T extends { id: string }>(modules: T[], activeIds: string[] | null): T[] {
  if (activeIds === null) return modules;
  const allowed = new Set(activeIds);
  return modules.filter(m => allowed.has(m.id));
}

export async function saveActiveModulesForTenant(tenantId: string, activeIds: string[]): Promise<boolean> {
  if (!tenantId) return false;
  const ids = Array.from(new Set(activeIds.filter(id => typeof id === "string" && id.trim().length > 0)));
  try {
    const res = await authFetch("/api/entitlements", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, activeModulesPatch: { activeModules: ids } }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Wendet die Tenant-Overrides auf die statische Modul-Liste an. */
export function applyPlanOverrides(modules: SecurityModuleMeta[], overrides: ModulePlanOverrides): SecurityModuleMeta[] {
  return modules.map(m => {
    const override = overrides[m.id];
    return override && override !== m.level ? { ...m, level: override } : m;
  });
}

/** Overrides auf Pläne, die es nicht mehr gibt, fallen weg – das Modul behält seine eigene Stufe (wie applyPlanOverrides in api/_modulePlan.js). */
function knownPlanOverrides(overrides: ModulePlanOverrides, plans: TrainingPlan[]): ModulePlanOverrides {
  const known = new Set(plans.map(p => p.id));
  return Object.fromEntries(Object.entries(overrides).filter(([, level]) => typeof level === "string" && known.has(level)));
}

export function getEffectiveModulesForLevel(level: TrainingLevel, overrides: ModulePlanOverrides, plans: TrainingPlan[] = FALLBACK_PLANS): SecurityModuleMeta[] {
  const all = applyPlanOverrides(SECURITY_MODULES, knownPlanOverrides(overrides, plans));
  const levelRank = userTrainingPlanRank(level, plans);
  return all.filter(m => trainingPlanRank(m.level, plans) <= levelRank);
}

export function getUserGroupsFromProfile(profile: UserGroupProfile | null): string[] {
  if (!profile) return [];
  return Array.from(new Set([
    ...(Array.isArray(profile.departments) ? profile.departments : []),
    profile.department ?? "",
  ].map(v => (v ?? "").trim()).filter(Boolean)));
}

/**
 * Module-Plan eines Users = Level-Module ∪ Module aller Gruppen, in denen der User ist.
 * Gruppen-Module ergänzen den Level-Plan um zusätzliche Module.
 */
export function getEffectiveModulesForUser(
  level: TrainingLevel,
  userGroups: string[],
  groupModules: Record<string, string[]>,
  overrides: ModulePlanOverrides,
  plans: TrainingPlan[] = FALLBACK_PLANS,
): SecurityModuleMeta[] {
  const base = getEffectiveModulesForLevel(level, overrides, plans);
  const extraIds = new Set<string>();
  userGroups.forEach(g => {
    const ids = groupModules?.[g];
    if (Array.isArray(ids)) ids.forEach(id => extraIds.add(id));
  });
  if (extraIds.size === 0) return base;
  const baseIds = new Set(base.map(m => m.id));
  const extras = applyPlanOverrides(SECURITY_MODULES, knownPlanOverrides(overrides, plans)).filter(m => extraIds.has(m.id) && !baseIds.has(m.id));
  return [...base, ...extras];
}

export type ModuleAssignmentSource =
  | { type: "level"; level: TrainingLevel }
  | { type: "group"; group: string };

export type AssignedModuleWithSources = {
  module: SecurityModuleMeta;
  sources: ModuleAssignmentSource[];
};

/**
 * Wie getEffectiveModulesForUser, aber mit Herkunft pro Modul:
 * Level-Plan und/oder eine oder mehrere Gruppen. Gleiche Reihenfolge/Menge
 * wie getEffectiveModulesForUser.
 */
export function getEffectiveModulesWithSources(
  level: TrainingLevel,
  userGroups: string[],
  groupModules: Record<string, string[]>,
  overrides: ModulePlanOverrides,
  plans: TrainingPlan[] = FALLBACK_PLANS,
): AssignedModuleWithSources[] {
  const groupsByModuleId = new Map<string, string[]>();
  userGroups.forEach(g => {
    const ids = groupModules?.[g];
    if (!Array.isArray(ids)) return;
    ids.forEach(id => {
      const list = groupsByModuleId.get(id) ?? [];
      if (!list.includes(g)) list.push(g);
      groupsByModuleId.set(id, list);
    });
  });

  const base = getEffectiveModulesForLevel(level, overrides, plans);
  const baseIds = new Set(base.map(m => m.id));
  const result: AssignedModuleWithSources[] = base.map(module => ({
    module,
    sources: [
      { type: "level" as const, level: module.level },
      ...(groupsByModuleId.get(module.id) ?? []).map(group => ({ type: "group" as const, group })),
    ],
  }));

  applyPlanOverrides(SECURITY_MODULES, knownPlanOverrides(overrides, plans)).forEach(module => {
    if (baseIds.has(module.id)) return;
    const groups = groupsByModuleId.get(module.id);
    if (!groups || groups.length === 0) return;
    result.push({ module, sources: groups.map(group => ({ type: "group" as const, group })) });
  });

  return result;
}
